import { BellOutlined } from "@ant-design/icons";
import { Badge, Button, Empty, List, Popover, Spin, Typography } from "antd";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { notificationApi } from "api/notification.api";
import { NotificationItem, useNotification } from "hooks/useNotification";
import { userStore } from "store/userStore";
import { syncPendingPlayerId } from "utils/oneSignal";

const { Text } = Typography;

const timeAgo = (createdAt: number) => {
  if (!createdAt) return "";
  const diff = Date.now() / 1000 - createdAt;
  if (diff < 60) return "vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  return `${Math.floor(diff / 86400)} ngày trước`;
};

export const NotificationBell = observer(() => {
  const {
    notifications,
    totalUnread,
    fetchNotification,
    fetchTotalUnread,
    loadingNotification,
  } = useNotification();
  const [open, setOpen] = useState(false);
  const [readingId, setReadingId] = useState<number | null>(null);

  useEffect(() => {
    if (!userStore.token) return;
    syncPendingPlayerId();
    fetchTotalUnread();
    const timer = setInterval(fetchTotalUnread, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.token]);

  useEffect(() => {
    if (open) fetchNotification(1, 20);
  }, [open ]);

  const handleRead = async (item: NotificationItem) => {
    try {
      setReadingId(item.id);
      await notificationApi.handleReadNotification(item.id);
      await Promise.all([fetchNotification(1, 20), fetchTotalUnread()]);
      if (item.url) window.open(item.url, "_blank");
      setOpen(false);
    } finally {
      setReadingId(null);
    }
  };

  const handleReadAll = async () => {
    await notificationApi.handleReadAllNotification();
    await Promise.all([fetchNotification(1, 20), fetchTotalUnread()]);
  };

  const content = (
    <div style={{ width: 360, maxWidth: "80vw" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Text strong>Thông báo</Text>
        <Button type="link" size="small" onClick={handleReadAll}>
          Đọc tất cả
        </Button>
      </div>
      <Spin spinning={loadingNotification}>
        {notifications.length === 0 ? (
          <Empty description="Chưa có thông báo" style={{ padding: 24 }} />
        ) : (
          <List
            style={{ maxHeight: 420, overflowY: "auto" }}
            dataSource={notifications}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                onClick={() => handleRead(item)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  background: item.isRead ? "#fff" : "#f6fffd",
                }}
              >
                <List.Item.Meta
                  title={
                    <Text strong={!item.isRead} style={{ fontSize: 13 }}>
                      {item.title}
                      {readingId === item.id ? " ..." : ""}
                    </Text>
                  }
                  description={
                    <>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.shortContent || item.content}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {timeAgo(item.createdAt)}
                      </Text>
                    </>
                  }
                />
                {!item.isRead && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#1fa6aa",
                      flexShrink: 0,
                    }}
                  />
                )}
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
    >
      <Badge count={totalUnread} overflowCount={99} size="small">
        <BellOutlined style={{ fontSize: 20, cursor: "pointer" }} />
      </Badge>
    </Popover>
  );
});
